import {DateTime} from "luxon";
import {OttopilotEventStatus, type OttopilotEvent} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import {OttopilotScheduleValueEncoding,
OttopilotTrigger,
OttopilotTriggerDataDriver} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {OttopilotTriggerConfig,
OttopilotTriggerDataColumn,
OttopilotTriggerElement} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {generateUuid, getCurrentIsoTimestamp} from "@vesta/global-common-typescript/utilities/utilities";
import {toIso8601FromIstSafe} from "~/utilities/utilities";

export type EditableEvent = OttopilotEvent;

export type EventContainerTriggerEventBuildContext = {
    eventContainerId: Uuid;
    eventContainerDispositionId: Uuid | null;
    heimdallUserId: Uuid;
    workspaceId: Uuid;
    workspaceUser: {
        userId: Uuid;
        templateId: Uuid;
    };
    selectedDispositionId: Uuid | null;
    /**
     * Event Container additional columns available when resolving {@link OttopilotTriggerDataDriver.runTimeDefault} with
     * `referenceFieldType: "OttopilotEventContainer"`. When omitted, those mappings are treated as absent.
     */
    eventContainerAdditionalColumnValues?: Record<Uuid, unknown>;
    /**
     * Resolve an OttopilotEvent additional column on this journey for {@link OttopilotTriggerDataDriver.runTimeDefault} with
     * `referenceFieldType: "OttopilotEvent"`. When omitted, those mappings are treated as absent.
     */
    getEventColumnValue?: (referenceField: Uuid) => unknown;
};

export function resolveActiveTrigger(
    triggerConfig: OttopilotTriggerConfig | undefined,
    dispositionId: Uuid | undefined | null,
): OttopilotTriggerElement | undefined {
    if (triggerConfig === undefined || triggerConfig.length === 0) {
        return undefined;
    }
    if (dispositionId === undefined || dispositionId === null) {
        return undefined;
    }
    for (const element of triggerConfig) {
        if (element.triggerDispositions.includes(dispositionId)) {
            return element;
        }
    }
    return undefined;
}

/** True when {@link EventContainerDetailsTriggerEvent} would render for this disposition. */
export function isTriggerEventFormVisible(
    triggerConfig: OttopilotTriggerConfig | undefined,
    dispositionId: Uuid | null,
): boolean {
    const activeTrigger = resolveActiveTrigger(triggerConfig, dispositionId);
    return activeTrigger !== undefined && activeTrigger.type === OttopilotTrigger.dateTimeTrigger;
}

export function getLatestPresentEventColumnValueFromEventContainerEvents(args: {
    events: Array<{additionalColumnValues: Record<Uuid, unknown>; updatedAt: string}>;
    referenceField: Uuid;
}): unknown {
    const sorted = [...args.events].sort((left, right) => {
        if (left.updatedAt < right.updatedAt) {
            return 1;
        }
        if (left.updatedAt > right.updatedAt) {
            return -1;
        }
        return 0;
    });
    for (const event of sorted) {
        if (Object.prototype.hasOwnProperty.call(event.additionalColumnValues, args.referenceField) !== true) {
            continue;
        }
        const value = event.additionalColumnValues[args.referenceField];
        if (value === undefined || value === null) {
            continue;
        }
        return value;
    }
    return undefined;
}

type TriggerColumnResolution =
    | {
          includeInPayload: true;
          value: unknown;
      }
    | {
          includeInPayload: false;
      };

function resolveDataColumnValue(
    entry: OttopilotTriggerDataColumn,
    context: EventContainerTriggerEventBuildContext,
): TriggerColumnResolution {
    switch (entry.driver) {
        case OttopilotTriggerDataDriver.staticDefault: {
            return {
                includeInPayload: true,
                value: entry.defaultValue,
            };
        }
        case OttopilotTriggerDataDriver.workspaceUser: {
            return {
                includeInPayload: true,
                value: context.workspaceUser.userId,
            };
        }
        case OttopilotTriggerDataDriver.workspaceUserTemplateId: {
            return {
                includeInPayload: true,
                value: context.workspaceUser.templateId,
            };
        }
        case OttopilotTriggerDataDriver.currentTimestamp: {
            return {
                includeInPayload: true,
                value: getCurrentIsoTimestamp(),
            };
        }
        case OttopilotTriggerDataDriver.eventContainerDisposition: {
            return {
                includeInPayload: true,
                value: context.selectedDispositionId,
            };
        }
        case OttopilotTriggerDataDriver.runTimeDefault: {
            let raw: unknown;
            if (entry.referenceFieldType === "OttopilotEventContainer") {
                const columns = context.eventContainerAdditionalColumnValues;
                if (columns === undefined) {
                    return {includeInPayload: false};
                }
                raw = columns[entry.referenceField];
            } else {
                const resolver = context.getEventColumnValue;
                if (resolver === undefined) {
                    return {includeInPayload: false};
                }
                raw = resolver(entry.referenceField);
            }
            if (raw === undefined || raw === null) {
                return {includeInPayload: false};
            }
            return {
                includeInPayload: true,
                value: raw,
            };
        }
        default: {
            throw new Error("Unhandled OttopilotTriggerDataDriver");
        }
    }
}

function buildEventAdditionalColumnValues(
    trigger: OttopilotTriggerElement,
    context: EventContainerTriggerEventBuildContext,
): Record<Uuid, unknown> {
    const additionalColumnValues: Record<Uuid, unknown> = {};
    for (const column of trigger.dataColumns) {
        if (column.columnType !== "OttopilotEvent") {
            continue;
        }
        const resolved = resolveDataColumnValue(column, context);
        if (resolved.includeInPayload !== true) {
            continue;
        }
        additionalColumnValues[column.columnId] = resolved.value;
    }
    return additionalColumnValues;
}

export function buildEventContainerAdditionalColumnValues(
    trigger: OttopilotTriggerElement,
    context: EventContainerTriggerEventBuildContext,
): Record<Uuid, unknown> {
    const additionalColumnValues: Record<Uuid, unknown> = {};
    for (const column of trigger.dataColumns) {
        if (column.columnType !== "OttopilotEventContainer") {
            continue;
        }
        const resolved = resolveDataColumnValue(column, context);
        if (resolved.includeInPayload !== true) {
            continue;
        }
        additionalColumnValues[column.columnId] = resolved.value;
    }
    return additionalColumnValues;
}

export function buildInitialEditableEventFromTrigger(
    trigger: OttopilotTriggerElement,
    eventTemplate: OttopilotEventTemplate,
    context: EventContainerTriggerEventBuildContext,
): EditableEvent {
    if (eventTemplate.id !== trigger.eventTemplateId) {
        throw new Error(`Trigger Event template mismatch: expected ${trigger.eventTemplateId}, received ${eventTemplate.id}`);
    }
    const additionalColumnValues = buildEventAdditionalColumnValues(trigger, context);
    additionalColumnValues[eventTemplate.configuration.dispositionColumnConfig.columnId] =
        eventTemplate.configuration.dispositionColumnConfig.defaultValue;
    return {
        id: generateUuid(),
        createdBy: context.heimdallUserId,
        createdAt: getCurrentIsoTimestamp(),
        updatedBy: context.heimdallUserId,
        updatedAt: getCurrentIsoTimestamp(),
        status: OttopilotEventStatus.active,
        templateId: trigger.eventTemplateId,
        eventContainerId: context.eventContainerId,
        additionalColumnValues: additionalColumnValues,
        revisionId: generateUuid(),
    };
}

export function duluxScheduleRawToApiString(raw: unknown): Iso8601DateTime {
    if (raw instanceof Date) {
        const istWall = DateTime.fromMillis(raw.getTime(), {zone: "utc"})
            .setZone("Asia/Kolkata")
            .toFormat("yyyy-MM-dd HH:mm:ss");
        const api = toIso8601FromIstSafe(istWall);
        if (api === null) {
            throw new Error("Invalid schedule date/time for Dulux encoding");
        }
        return api as Iso8601DateTime;
    }
    if (typeof raw === "string") {
        const direct = toIso8601FromIstSafe(raw);
        if (direct !== null) {
            return direct as Iso8601DateTime;
        }
        const fromIso = DateTime.fromISO(raw, {setZone: true});
        if (fromIso.isValid) {
            const istWall = fromIso.setZone("Asia/Kolkata").toFormat("yyyy-MM-dd HH:mm:ss");
            const api = toIso8601FromIstSafe(istWall);
            if (api === null) {
                throw new Error("Invalid schedule date/time for Dulux encoding");
            }
            return api as Iso8601DateTime;
        }
        throw new Error("Schedule date/time is required");
    }
    throw new Error("Schedule date/time is required");
}

/** Mantine schedule picker -> UTC ISO8601 for trigger event column storage. */
export function triggerSchedulePickerValueToIso8601DateTimeUtc(value: Date | string | null): Iso8601DateTime | null {
    if (value === null) {
        return null;
    }
    const date = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(date.getTime()) === true) {
        return null;
    }
    return date.toISOString() as Iso8601DateTime;
}

/** Reads stored schedule value (ISO string or legacy `Date`) for the picker. */
export function iso8601DateTimeUtcToTriggerSchedulePickerValue(raw: unknown): Date | null {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (raw instanceof Date) {
        if (Number.isNaN(raw.getTime()) === true) {
            return null;
        }
        return raw;
    }
    if (typeof raw === "string") {
        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime()) === true) {
            return null;
        }
        return parsed;
    }
    return null;
}

function iso8601ScheduleRawToApiString(raw: unknown): string {
    if (raw instanceof Date) {
        return raw.toISOString();
    }
    if (typeof raw === "string") {
        return raw;
    }
    throw new Error("Schedule call date/time is required");
}

export function serializeTriggerScheduleValue(
    trigger: OttopilotTriggerElement,
    rawScheduleValue: unknown,
): string {
    if (trigger.type !== OttopilotTrigger.dateTimeTrigger) {
        throw new Error("serializeTriggerScheduleValue expects a dateTimeTrigger");
    }
    if (rawScheduleValue === null || rawScheduleValue === undefined) {
        throw new Error("Schedule date/time is required");
    }
    switch (trigger.scheduleValueEncoding) {
        case OttopilotScheduleValueEncoding.duluxIst: {
            return duluxScheduleRawToApiString(rawScheduleValue);
        }
        case OttopilotScheduleValueEncoding.iso8601Instant: {
            return iso8601ScheduleRawToApiString(rawScheduleValue);
        }
        default: {
            const _: never = trigger.scheduleValueEncoding;
            void _;
            throw new Error("Unhandled OttopilotScheduleValueEncoding");
        }
    }
}

export function mergeSerializedScheduleIntoEvent(
    trigger: OttopilotTriggerElement,
    triggerEvent: EditableEvent,
): EditableEvent {
    if (trigger.type !== OttopilotTrigger.dateTimeTrigger) {
        return triggerEvent;
    }
    const raw = triggerEvent.additionalColumnValues[trigger.dateTimeColumnId];
    const serialized = serializeTriggerScheduleValue(trigger, raw);
    return {
        ...triggerEvent,
        additionalColumnValues: {
            ...triggerEvent.additionalColumnValues,
            [trigger.dateTimeColumnId]: serialized,
        },
    };
}
